import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Navigator, UNSAFE_NavigationContext as NavigationContext, useLocation } from 'react-router-dom';

const safeNavigationContext = NavigationContext ?? createContext<{ navigator: Navigator }>(null!);

interface PendingNavigationTransaction {
    location: { pathname: string; search: string };
    retry: () => void;
}

type UseNavigationGuardResult = {
    showLeaveModal: boolean;
    handleConfirmLeave: () => void;
    handleCancelLeave: () => void;
};

type UseNavigationGuardOptions = {
    shouldAllowNavigation?: (nextLocation: { pathname: string; search: string }) => boolean;
};

type BlockableNavigator = Navigator & {
    block: (blocker: (transition: PendingNavigationTransaction) => void) => () => void;
};

const useNavigationGuard = (hasUnsavedChanges: boolean, options?: UseNavigationGuardOptions): UseNavigationGuardResult => {
    // In mono, the context provider is absent so useContext returns null — use optional chaining to avoid a destructuring crash.
    const navigator = useContext(safeNavigationContext)?.navigator;
    const currentPathname = useLocation().pathname;
    const currentPathnameRef = useRef(currentPathname);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<PendingNavigationTransaction>();
    const unblockNavigationRef = useRef<(() => void) | undefined>(undefined);

    const shouldAllowNavigationRef = useRef(options?.shouldAllowNavigation);
    shouldAllowNavigationRef.current = options?.shouldAllowNavigation;

    useEffect(() => {
        currentPathnameRef.current = currentPathname;
    }, [currentPathname]);

    useEffect(() => {
        if (!hasUnsavedChanges || !navigator) {
            return undefined;
        }

        let unblock: (() => void) | undefined;

        const registerBlock = () => {
            unblock = (navigator as BlockableNavigator).block((transition) => {
                const shouldAllow = shouldAllowNavigationRef.current
                    ? shouldAllowNavigationRef.current(transition.location)
                    : transition.location.pathname === currentPathnameRef.current;

                if (shouldAllow) {
                    unblock?.();
                    transition.retry();
                    registerBlock();
                    return;
                }
                setPendingNavigation(transition);
                setShowLeaveModal(true);
            });
            unblockNavigationRef.current = unblock;
        };

        registerBlock();

        return () => {
            unblock?.();
            unblockNavigationRef.current = undefined;
        };
    }, [hasUnsavedChanges, navigator]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges) {
                return;
            }
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleConfirmLeave = () => {
        setShowLeaveModal(false);
        unblockNavigationRef.current?.();
        unblockNavigationRef.current = undefined;
        pendingNavigation?.retry();
        setPendingNavigation(undefined);
    };

    const handleCancelLeave = () => {
        setShowLeaveModal(false);
        setPendingNavigation(undefined);
    };

    return { showLeaveModal, handleConfirmLeave, handleCancelLeave };
};

export default useNavigationGuard;
